"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseClient = void 0;
const axios_1 = __importDefault(require("axios"));
const form_data_1 = __importDefault(require("form-data"));
const utils_1 = require("./utils");
let fs;
if (typeof window === "undefined") {
    fs = require("fs");
}
function handleRequestError(response) {
    if (response.status < 400) {
        return;
    }
    let message;
    const errorContent = (0, utils_1.ensureCamelCase)(response.data);
    if (typeof errorContent === "object" && errorContent !== null) {
        message =
            errorContent.message ||
                (errorContent.detail && errorContent.detail.message) ||
                (typeof errorContent.detail === "string" && errorContent.detail) ||
                JSON.stringify(errorContent);
    }
    else {
        message = String(errorContent);
    }
    throw new Error(`Status ${response.status}: ${message}`);
}
class BaseClient {
    constructor(baseURL = "https://api.sciphi.ai", prefix = "", anonymousTelemetry = true, enableAutoRefresh = false) {
        this.baseUrl = `${baseURL}${prefix}`;
        this.accessToken = null;
        this.apiKey = process.env.R2R_API_KEY || null;
        this.refreshToken = null;
        this.anonymousTelemetry = anonymousTelemetry;
        this.enableAutoRefresh = enableAutoRefresh;
        this.axiosInstance = axios_1.default.create({
            baseURL: this.baseUrl,
            headers: {
                "Content-Type": "application/json",
            },
        });
    }
    async _makeRequest(method, endpoint, options = {}, version = "v3") {
        const url = `/${version}/${endpoint}`;
        const config = Object.assign(Object.assign({ method,
            url, headers: Object.assign({}, options.headers), params: options.params }, options), { responseType: options.responseType || "json" });
        config.headers = config.headers || {};
        if (options.params) {
            config.paramsSerializer = (params) => {
                return Object.entries(params)
                    .map(([key, value]) => {
                    if (Array.isArray(value)) {
                        return value
                            .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
                            .join("&");
                    }
                    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
                })
                    .join("&");
            };
        }
        if (options.data) {
            if (typeof form_data_1.default !== "undefined" && options.data instanceof form_data_1.default) {
                config.data = options.data;
                delete config.headers["Content-Type"];
            }
            else if (typeof options.data === "object") {
                if (config.headers["Content-Type"] === "application/x-www-form-urlencoded") {
                    config.data = Object.keys(options.data)
                        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(options.data[key])}`)
                        .join("&");
                }
                else {
                    config.data = JSON.stringify(options.data);
                    if (method !== "DELETE") {
                        config.headers["Content-Type"] = "application/json";
                    }
                    else {
                        config.headers["Content-Type"] = "application/json";
                        config.data = JSON.stringify(options.data);
                    }
                }
            }
            else {
                config.data = options.data;
            }
        }
        if (this.accessToken && this.apiKey) {
            throw new Error("Cannot have both access token and api key.");
        }
        if (this.apiKey &&
            !["register", "login", "verify_email", "health"].includes(endpoint)) {
            config.headers["x-api-key"] = this.apiKey;
        }
        else if (this.accessToken &&
            !["register", "login", "verify_email", "health"].includes(endpoint)) {
            config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        if (options.responseType === "stream") {
            return this.handleStreamingRequest(method, version, endpoint, config);
        }
        try {
            const response = await this.axiosInstance.request(config);
            if (options.responseType === "blob") {
                return response.data;
            }
            else if (options.responseType === "arraybuffer") {
                if (options.returnFullResponse) {
                    return response;
                }
                return response.data;
            }
            const responseData = options.returnFullResponse
                ? Object.assign(Object.assign({}, response), { data: (0, utils_1.ensureCamelCase)(response.data) }) : (0, utils_1.ensureCamelCase)(response.data);
            return responseData;
        }
        catch (error) {
            if (axios_1.default.isAxiosError(error) && error.response) {
                handleRequestError(error.response);
            }
            throw error;
        }
    }
    async handleStreamingRequest(method, version, endpoint, config) {
        var _a;
        const fetchHeaders = {};
        // Convert Axios headers to Fetch headers
        Object.entries(config.headers || {}).forEach(([key, value]) => {
            if (typeof value === "string") {
                fetchHeaders[key] = value;
            }
        });
        try {
            const response = await fetch(`${this.baseUrl}/${version}/${endpoint}`, {
                method,
                headers: fetchHeaders,
                body: config.data,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`HTTP error! status: ${response.status}: ${(0, utils_1.ensureCamelCase)(errorData).message || "Unknown error"}`);
            }
            // Create a TransformStream to process the response
            const transformStream = new TransformStream({
                transform(chunk, controller) {
                    // Process each chunk here if needed
                    controller.enqueue(chunk);
                },
            });
            // Pipe the response through the transform stream
            const streamedResponse = (_a = response.body) === null || _a === void 0 ? void 0 : _a.pipeThrough(transformStream);
            if (!streamedResponse) {
                throw new Error("No response body received from stream");
            }
            return streamedResponse;
        }
        catch (error) {
            console.error("Streaming request failed:", error);
            throw error;
        }
    }
    _ensureAuthenticated() {
        if (!this.accessToken) {
            throw new Error("Not authenticated. Please login first.");
        }
    }
    setTokens(accessToken, refreshToken) {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
    }
    setApiKey(apiKey) {
        if (!apiKey)
            throw new Error("API key is required");
        this.apiKey = apiKey;
    }
}
exports.BaseClient = BaseClient;
//# sourceMappingURL=baseClient.js.map